import { makeObservable, observable, action } from 'mobx';
import { FactorEnrollmentStatus } from '@wix/ambassador-identity-factors-v1-identity-factor/types';
import { WebAuthnApi } from '../services/webAuthnApi';
import {
  EnrollmentStatus,
  PublicKeyCredentialWithJSON,
} from '../types/webAuthnEnrollmentTypes';
import { CreatePasskeyErrorCode } from '../types/createPasskeyMessages';
import { isWebAuthnSupported } from '../utils/webauthnCompat';
import { NotificationStore } from '../utils/notificationStore';
import type { AppStore } from './AppStore';
import { HttpError } from '@wix/http-client';
import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaErrorSeverity,
  PanoramaPlatform,
} from '@wix/panorama-client-web';
import type { PanoramaClientForComponent } from '@wix/panorama-client-web';

const INVALID_PASSWORD_ERROR_CODE = 9960;

const panoramaClient: PanoramaClientForComponent | undefined = (() => {
  try {
    return panoramaClientFactory({
      baseParams: {
        fullArtifactId: 'com.wixpress.webauthn-management',
        platform: PanoramaPlatform.Standalone,
      },
    })
      .withGlobalConfig(createGlobalConfig())
      .client({ baseParams: { componentId: 'passkey-enrollment' } })
      .createClientForComponent();
  } catch {
    return undefined;
  }
})();

const PANORAMA_SEVERITY: Record<string, PanoramaErrorSeverity> = {
  [CreatePasskeyErrorCode.CANCELLED]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.DUPLICATE_PASSKEY]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.NOT_SUPPORTED]: PanoramaErrorSeverity.Warning,
  [CreatePasskeyErrorCode.TIMEOUT]: PanoramaErrorSeverity.Error,
  [CreatePasskeyErrorCode.GENERAL]: PanoramaErrorSeverity.Error,
  WRONG_PASSWORD: PanoramaErrorSeverity.Warning,
};

function reportToPanorama(error: unknown, errorName: string): void {
  try {
    panoramaClient
      ?.errorMonitor()
      .reportError(error instanceof Error ? error : new Error(String(error)), {
        errorName,
        severity: PANORAMA_SEVERITY[errorName] ?? PanoramaErrorSeverity.Error,
      });
  } catch {
    // Intentional: Do not break the user flow on monitoring errors
  }
}

export class WebAuthnEnrollmentStore {
  private readonly appStore?: AppStore;
  private readonly webAuthnApi: WebAuthnApi;

  enrollmentStatus: EnrollmentStatus = 'idle';
  passwordError: string | null = null;
  passwordStepNotification: NotificationStore = new NotificationStore();

  constructor(appStore?: AppStore) {
    this.appStore = appStore;
    this.webAuthnApi = new WebAuthnApi();

    makeObservable(this, {
      enrollmentStatus: observable,
      passwordError: observable,
      passwordStepNotification: observable,
      verifyPassword: action.bound,
      enrollWebAuthn: action.bound,
      sendForgotPasswordEmail: action.bound,
      clearPasswordError: action.bound,
      reset: action.bound,
    });
  }

  async verifyPassword(password: string): Promise<boolean> {
    if (!password) {
      this.passwordError =
        'passkey.popups.creation.confirmModal.error.emptyPassword';
      return false;
    }
    if (!this.appStore) {
      throw new Error('verifyPassword requires AppStore');
    }
    this.enrollmentStatus = 'authenticating';
    this.passwordError = null;

    try {
      await this.appStore.accountSettingsApi.authenticate(password);
      return true;
    } catch (error) {
      this.enrollmentStatus = 'error';
      const errorCode = (error as HttpError).response?.data?.errorCode;
      if (errorCode === INVALID_PASSWORD_ERROR_CODE) {
        this.passwordStepNotification.close();
        this.passwordError =
          'passkey.popups.creation.confirmModal.error.wrongPassword';
        reportToPanorama(error, 'WRONG_PASSWORD');
      } else {
        this.passwordStepNotification.open({
          content: 'passkey.popups.creation.confirmModal.error.general',
          theme: 'error',
        });
        reportToPanorama(error, CreatePasskeyErrorCode.GENERAL);
      }
      return false;
    }
  }

  async enrollWebAuthn(): Promise<
    | { success: true; isBackedUp: boolean }
    | {
        success: false;
        errorCode: CreatePasskeyErrorCode;
        errorMessage: string;
      }
  > {
    this.enrollmentStatus = 'enrolling';

    if (!isWebAuthnSupported()) {
      this.enrollmentStatus = 'error';
      reportToPanorama(
        new Error('WebAuthn not supported'),
        CreatePasskeyErrorCode.NOT_SUPPORTED,
      );
      return {
        success: false,
        errorCode: CreatePasskeyErrorCode.NOT_SUPPORTED,
        errorMessage: 'WebAuthn is not supported in this browser',
      };
    }

    try {
      const challengeResponse = await this.webAuthnApi.enrollWebAuthn();

      if (
        challengeResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_REQUIRES_ADDITIONAL_STEP
      ) {
        throw new Error('Unexpected enrollment status');
      }

      const credentialCreationData =
        challengeResponse.enrollmentRequiresAdditionalStepData?.webauthnData;

      if (!credentialCreationData?.publicKeyCredentialCreationOptionsJson) {
        throw new Error('Missing WebAuthn creation options');
      }

      const { publicKey } = JSON.parse(
        credentialCreationData.publicKeyCredentialCreationOptionsJson,
      );
      const parsedCreationOptions = (
        PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
      ).parseCreationOptionsFromJSON(publicKey);

      const newCredential = (await navigator.credentials.create({
        publicKey: parsedCreationOptions,
      })) as PublicKeyCredential | null;

      if (!newCredential) {
        throw new Error('Credential creation cancelled');
      }

      const completionResponse = await this.webAuthnApi.enrollWebAuthn(
        newCredential,
      );

      if (
        completionResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_COMPLETE
      ) {
        throw new Error('Enrollment failed');
      }

      const isBackedUp = this.extractBackupState(newCredential);

      this.enrollmentStatus = 'success';
      return { success: true, isBackedUp };
    } catch (error) {
      this.enrollmentStatus = 'error';
      const { errorCode, errorMessage } = this.parseEnrollmentError(error);
      reportToPanorama(error, errorCode);
      return {
        success: false,
        errorCode,
        errorMessage,
      };
    }
  }

  private extractBackupState(credential: PublicKeyCredential): boolean {
    try {
      const response = credential.response as AuthenticatorAttestationResponse;
      const authenticatorData = response.getAuthenticatorData();
      const flags = new Uint8Array(authenticatorData)[32];
      // Bit 4 (0x10) is the BS (Backup State) flag
      return (flags & 0x10) !== 0;
    } catch {
      return false;
    }
  }

  private parseEnrollmentError(error: unknown): {
    errorCode: CreatePasskeyErrorCode;
    errorMessage: string;
  } {
    if (error instanceof DOMException) {
      const errorMessage = `${error.name}: ${error.message}`;

      if (error.name === 'InvalidStateError') {
        return {
          errorCode: CreatePasskeyErrorCode.DUPLICATE_PASSKEY,
          errorMessage,
        };
      }

      if (error.name === 'NotAllowedError') {
        return {
          errorCode: CreatePasskeyErrorCode.CANCELLED,
          errorMessage,
        };
      }

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        return {
          errorCode: CreatePasskeyErrorCode.TIMEOUT,
          errorMessage,
        };
      }

      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage,
      };
    }

    if (error instanceof Error) {
      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage: `Error: ${error.message}`,
      };
    }

    return {
      errorCode: CreatePasskeyErrorCode.GENERAL,
      errorMessage: `Unknown: ${String(error)}`,
    };
  }

  async sendForgotPasswordEmail(): Promise<void> {
    if (!this.appStore) {
      throw new Error('sendForgotPasswordEmail requires AppStore');
    }
    const email = this.appStore.userDetails?.email;
    if (!email) {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
      return;
    }

    try {
      await this.appStore.accountSettingsApi.sendResetPasswordEmail(email);
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.success.notification',
        theme: 'success',
      });
    } catch {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
    }
  }

  clearPasswordError(): void {
    this.passwordError = null;
  }

  reset(): void {
    this.enrollmentStatus = 'idle';
    this.passwordError = null;
    this.passwordStepNotification.close();
  }
}
